import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, UncontrolledTooltip } from "reactstrap";
import Field from "../../../../components/Field";
import DurationInput, { getDurationInputValue } from "../../../../components/Inputs/DurationInput";
import ManagersSelect from "../../../../components/Inputs/ManagersSelect";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import WeekdaySelect from "../../../../components/Inputs/WeekdaySelect";
import { useGetRoleByCode } from "../../../../utils/api/role";
import { renderDurationAsFormat } from "../../../../utils/helpers/date";
import {
	getSettingsResourceSearchFilters,
	getSettingsResourceSearchFiltersWithLookups,
	renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";

function ExtendedDurationInput({ value: _value, onChange: _onChange, ...props }) {
	const value = useMemo(() => {
		return renderDurationAsFormat(_value || 0, "HH:mm");
	}, [_value]);

	const onChange = useCallback(
		(value) => {
			if (!_onChange) {
				return;
			}
			_onChange(getDurationInputValue(value));
		},
		[_onChange]
	);

	return <DurationInput value={value} onChange={onChange} {...props} />;
}

function JobInformation({ form, loadingCalculationGroup }) {
	const { t } = useTranslation();

	const {
		data: defaultRole,
		loading: loadingDefaultRole,
		fetch: fetchDefaultRole,
	} = useGetRoleByCode();

	useEffect(() => {
		fetchDefaultRole("Employee");
	}, [fetchDefaultRole]);

	useEffect(() => {
		const value = form.getFieldValue("roles");
		if (value || !defaultRole) {
			return;
		}

		form.setFieldsValue({
			roles: [defaultRole],
		});
	}, [form, defaultRole]);

	return (
		<Row>
			<ManagersSelect form={form} isSelf={false} md={6} sm={12} />

			<Col sm={12} md={6}>
				<Field name="jobGroup" label={t("job-group")}>
					<ResourceSelect
						labelPropName="description"
						resourcePath="/job-groups"
						renderLabel={renderSettingsResourceLabel}
						placeholder={t("job-group")}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						allowClear
					/>
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="jobs"
					label={t("job")}
					rules={[
						{
							required: true,
							message: t("required-job"),
						},
					]}
				>
					<ResourceSelect
						labelPropName="description"
						resourcePath="/jobs"
						renderLabel={renderSettingsResourceLabel}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						placeholder={t("job")}
					/>
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<BaseField shouldUpdate noStyle>
					{({ }, { }, { getFieldValue, setFieldValue }) => {
						return (
							<Field
								name="primaryLocation"
								label={t("primary-location")}
								rules={[
									{
										required: true,
										message: t("required-primary-location"),
									},
								]}
							>
								<ResourceSelect
									resourcePath="/locations"
									renderLabel={renderSettingsResourceLabel}
									placeholder={t("primary-location")}
									hasSearch
									getSearchFilters={getSettingsResourceSearchFiltersWithLookups}
									onChange={(value) => {
										let otherLocations = getFieldValue("otherLocations") || [];
										otherLocations = otherLocations.filter((item) => item.id !== value.id);
										setFieldValue("otherLocations", otherLocations);
									}}
								/>
							</Field>
						);
					}}
				</BaseField>
			</Col>

			<Col sm={12} md={6}>
				<BaseField shouldUpdate noStyle>
					{({ }, { }, { getFieldValue }) => {
						const primaryLocation = getFieldValue("primaryLocation");
						let resourcePath = "/locations";
						if (primaryLocation) {
							resourcePath += `?excluded=${primaryLocation.id}`;
						}
						return (
							<Field
								name="otherLocations"
								label={t("other-locations")}
							>
								<ResourceSelect
									mode="multiple"
									resourcePath={resourcePath}
									renderLabel={renderSettingsResourceLabel}
									placeholder={t("other-locations")}
									hasSearch
									getSearchFilters={getSettingsResourceSearchFiltersWithLookups}
								/>
							</Field>
						);
					}}
				</BaseField>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="payGroup"
					label={t("pay-group")}
					rules={[
						{
							required: true,
							message: t("required-pay-group"),
						},
					]}
				>
					<ResourceSelect
						labelPropName="description"
						resourcePath="/pay-group"
						placeholder={t("pay-group")}
						renderLabel={renderSettingsResourceLabel}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="calculationGroup"
					label={t("calculation-group")}
					rules={[
						{
							required: true,
							message: t("required-calculation-group"),
						},
					]}
				>
					<ResourceSelect
						labelPropName="description"
						resourcePath="/calculation-group"
						renderLabel={renderSettingsResourceLabel}
						placeholder={t("calculation-group")}
						loading={loadingCalculationGroup}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="roles"
					label={t("roles")}
					className="col-span-2"
					rules={[
						{
							required: true,
							message: t("required-roles"),
						},
					]}
				>
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/user-roles"
						mode="multiple"
						placeholder={t("roles")}
						loading={loadingDefaultRole}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFiltersWithLookups}
					/>
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="expectedHours"
					label={t("expected-hours")}
				>
					<ExtendedDurationInput />
				</Field>
			</Col>

			<Col sm={12} md={6}>
				<Field
					name="expectedWorkingDays"
					label={
						<>
							<span className="d-inline-flex align-items-center gap-x-1">
								<span>{t("Days")}</span>
								<FontAwesomeIcon
									id="expectedWorkingDaysInfo"
									className="cursor-pointer"
									icon={faInfoCircle}
								/>
							</span>
							<UncontrolledTooltip target="#expectedWorkingDaysInfo">
								{t("expected-working-days-will-default-to-monday-to-friday-if-not-set")}
							</UncontrolledTooltip>
						</>
					}
				>
					<WeekdaySelect mode="multiple" />
				</Field>
			</Col>
		</Row>
	);
}

export default JobInformation;
